"use client";

import { ReferralAuthInstructionsCard } from "@/src/components/tasks/referral-auth-instructions-card";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { useReferralAuthOpenAuthorization } from "@/src/hooks/use-referral-auth-open-authorization";
import { deriveReferralAuthorizationFromStedi } from "@/src/lib/clearinghouse/stedi/derive-referral-authorization-from-stedi";
import { resolveStediEligibilityDisplay } from "@/src/lib/clearinghouse/stedi/extract-eligibility-summary";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import {
  buildAutonomyReferralAuthInstruction,
  canStartReferralAuthTrackingCase,
  getReferralAuthTrackingCaseButtonTooltip,
} from "@/src/lib/prior-authorization/autonomy-referral-auth-instructions";
import { resolveReferralAuthOnFile } from "@/src/lib/prior-authorization/referral-auth-on-file";
import type { PriorAuthorization } from "@/src/models/documents/prior-authorization/prior-authorization";
import { Referral } from "@/src/models/documents/referral";

interface AutonomyReferralAuthInstructionsProps {
  referral: Referral;
  linkedPriorAuthorizations: PriorAuthorization[];
  taskId: string;
  taskPriorAuthorization?: PriorAuthorization | null;
  startTrackingCaseDisabled?: boolean;
}

export function AutonomyReferralAuthInstructions({
  referral,
  linkedPriorAuthorizations,
  taskId,
  taskPriorAuthorization,
  startTrackingCaseDisabled = false,
}: AutonomyReferralAuthInstructionsProps) {
  const { eligibilityCheck, isLoading } = useStediEligibilityCheck(referral.id, referral.primaryCoverage);
  const eligibilityDisplay = eligibilityCheck ? resolveStediEligibilityDisplay(eligibilityCheck) : null;
  const teStatus = eligibilityDisplay
    ? deriveReferralAuthorizationFromStedi(eligibilityDisplay.officeVisitRequiresAuth, eligibilityDisplay.checkFailed)
        .teStatus
    : null;
  const authOnFile = resolveReferralAuthOnFile(referral, linkedPriorAuthorizations, taskPriorAuthorization);
  const canStartTrackingCase = canStartReferralAuthTrackingCase({
    isLoading,
    hasEligibilityCheck: !!eligibilityCheck,
    teStatus,
    planType: eligibilityDisplay?.planType ?? null,
  });
  const { handleOpenAuthorization, isOpeningAuthorization } = useReferralAuthOpenAuthorization({
    taskId,
    taskPriorAuthorization,
    linkedPriorAuthorizations,
  });
  const isButtonDisabled = startTrackingCaseDisabled || isOpeningAuthorization || !canStartTrackingCase;
  const openButtonTooltip = getReferralAuthTrackingCaseButtonTooltip({
    isLoading,
    hasEligibilityCheck: !!eligibilityCheck,
    teStatus,
    planType: eligibilityDisplay?.planType ?? null,
    isSubmitting: startTrackingCaseDisabled,
  });

  return (
    <div className="flex flex-col gap-4">
      <ReferralAuthInstructionsCard
        instruction={buildAutonomyReferralAuthInstruction({
          isLoading,
          hasEligibilityCheck: !!eligibilityCheck,
          teStatus,
          planType: eligibilityDisplay?.planType ?? null,
          authOnFile,
        })}
        openButtonTooltip={openButtonTooltip}
        isOpenButtonDisabled={isButtonDisabled}
        isOpening={isOpeningAuthorization}
        onOpenAuthorization={handleOpenAuthorization}
      />
      <ReferralEligibilityCard
        primaryCoverage={referral.primaryCoverage}
        secondaryCoverage={referral.secondaryCoverage}
        referralId={referral.id}
        taskId={taskId}
      />
    </div>
  );
}
